import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../SoapForm.css";
import {
  saveSoapNote,
  updateSoapNote,
  completeSoapNote,
  approveSoapNote,
  unapproveSoapNote,
  fetchPrecedingSoapNote,
  fetchAppointmentById,
} from "../../store/thunks/appointmentsThunk";
import { XMarkIcon, BeakerIcon } from "@heroicons/react/24/outline";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import heidiService from "../../utils/heidiService";
import { toast } from 'react-toastify';
import ConfirmDialog from './ConfirmDialog';

const SOAPForm = ({ onClose, appointment: initialAppointment }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [appointment, setAppointment] = useState(initialAppointment);
  const [isNewSoapNote, setIsNewSoapNote] = useState(true);
  const [isFreeform, setIsFreeform] = useState(false);
  const [soapData, setSoapData] = useState([]);
  const [structuredData, setStructuredData] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });
  const [isSignedOff, setIsSignedOff] = useState(false);
  const [isPopulated, setIsPopulated] = useState(false);
  const [showApprovalView, setShowApprovalView] = useState(false);
  const [supervisorComment, setSupervisorComment] = useState("");
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);
  const [heidiLoading, setHeidiLoading] = useState(false);
  
  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  // Unapprove SOAP states
  const [showUnapproveModal, setShowUnapproveModal] = useState(false);
  const [unapproveReason, setUnapproveReason] = useState("");

  useEffect(() => {
    if (appointment && appointment.soapNote) {
      setIsNewSoapNote(appointment.soapNote.length === 0);

      // Determine if user can approve the SOAP note
      const userCanApprove = user.specialization?.includes("SLP") ||
                            (appointment.supervisingClinician && user._id === appointment.supervisingClinician);

      // Show approval view if completed but not finalized AND user can approve it
      // This allows supervisors to see the comments textarea
      if (appointment.soapNoteCompleted && !appointment.soapNoteFinalized && userCanApprove) {
        setShowApprovalView(true);
      } else {
        // For all other cases (new notes, approved notes, or non-supervisors), show edit view
        setShowApprovalView(false);
      }
    }
    // Reset supervisor comment and edit state when appointment changes
    setSupervisorComment("");
    setIsEditingCompleted(false);
  }, [appointment, user]);

  useEffect(() => {
    if (appointment?.soapNote?.length > 0) {
      const latestNote = appointment.soapNote[appointment.soapNote.length - 1];
      setIsSignedOff(!!latestNote.signedOffBy);

      // Look for supervisor comment in all SOAP notes, prioritizing the latest one with a comment
      let foundSupervisorComment = "";

      // Check from latest to oldest for supervisor comment
      for (let i = appointment.soapNote.length - 1; i >= 0; i--) {
        const note = appointment.soapNote[i];
        if (note.supervisorComment && note.supervisorComment.trim()) {
          foundSupervisorComment = note.supervisorComment;
          console.log(
            "Found supervisor comment in note",
            i,
            ":",
            foundSupervisorComment
          );
          break;
        }
      }

      if (foundSupervisorComment) {
        setSupervisorComment(foundSupervisorComment);
      } else {
        console.log(
          "No supervisor comment found in any SOAP note. All notes:",
          appointment.soapNote
        );
        setSupervisorComment("");
      }

      if (isPopulated || !isNewSoapNote) {
        if (latestNote.templateUsed === "freeform") {
          setIsFreeform(true);
          const freeformFields = Object.entries(latestNote.noteData).map(
            ([name, value]) => ({ id: Date.now() + Math.random(), name, value })
          );
          setSoapData(freeformFields);
        } else if (latestNote.templateUsed === "structured") {
          setIsFreeform(false);
          setStructuredData(latestNote.noteData || {});
        }
      }
    }
  }, [appointment, isPopulated, isNewSoapNote]);

  const handleAddField = () => {
    setSoapData((prev) => [...prev, { id: Date.now(), name: "", value: "" }]);
  };

  const handlePopulateSoap = async () => {
    if (appointment.blockId && appointment.blockNumber > 1) {
      try {
        const response = await dispatch(
          fetchPrecedingSoapNote(appointment._id)
        ).unwrap();

        if (response) {
          const { noteData, templateUsed } = response;
          if (templateUsed === "freeform") {
            setIsFreeform(true);
            const freeformFields = Object.entries(noteData).map(
              ([name, value]) => ({
                id: Date.now() + Math.random(),
                name,
                value,
              })
            );
            setSoapData(freeformFields);
          } else if (templateUsed === "structured") {
            setIsFreeform(false);
            setStructuredData(noteData);
          }
          setIsPopulated(true);
        }
      } catch (error) {
        console.error("Failed to fetch preceding SOAP note:", error);
        toast.warning("No SOAP note found in the preceding appointment.");
      }
    } else {
      toast.info(
        "Cannot populate SOAP note. This appointment is the first in the block."
      );
    }
  };

  const handleSave = async () => {
    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      if (isNewSoapNote) {
        await dispatch(saveSoapNote([appointment, user, note])).unwrap();
        toast.success("SOAP Note Saved.");
      } else {
        await dispatch(updateSoapNote([appointment, user, note])).unwrap();
        toast.success("SOAP Note Updated.");
      }
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      setAppointment(updatedAppointment);
    } catch (error) {
      console.error("Save/Update failed:", error);
      toast.error(
        "An error occurred while saving/updating the SOAP Note. Please try again."
      );
    }
  };

  const handleCompleteSoapNote = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to save and complete the SOAP Note? Once completed, it cannot be edited."
    );
    if (!confirmed) return;

    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      if (isNewSoapNote) {
        await dispatch(saveSoapNote([appointment, user, note])).unwrap();
      } else {
        await dispatch(updateSoapNote([appointment, user, note])).unwrap();
      }
      const completedResponse = await dispatch(
        completeSoapNote([appointment, user])
      ).unwrap();
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();

      // Update the local state with the new appointment data
      setAppointment(updatedAppointment);
      setShowApprovalView(true);

      toast.success("SOAP Note Completed. You can now approve it.");
    } catch (error) {
      console.error("Error completing SOAP note:", error);
      toast.error(
        "An error occurred while completing the SOAP Note. Please try again."
      );
    }
  };

  const handleCompleteAndApproveSoapNote = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to save, complete, and approve the SOAP Note? Once approved, it cannot be edited."
    );
    if (!confirmed) return;

    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      // Step 1: Save the note
      if (isNewSoapNote) {
        await dispatch(saveSoapNote([appointment, user, note])).unwrap();
      } else {
        await dispatch(updateSoapNote([appointment, user, note])).unwrap();
      }

      // Step 2: Complete the note
      await dispatch(completeSoapNote([appointment, user])).unwrap();

      // Step 3: Approve the note
      const finalNote = {
        noteData: note.noteData,
        templateUsed: note.templateUsed,
      };

      await dispatch(
        approveSoapNote({
          appointment,
          user,
          finalNote,
          supervisorComment: "", // SLP users don't need supervisor comments
        })
      ).unwrap();

      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      setAppointment(updatedAppointment);

      toast.success("SOAP Note completed and approved successfully.");
      onClose();
    } catch (error) {
      console.error("Error completing and approving SOAP note:", error);
      toast.error(
        "An error occurred while completing and approving the SOAP Note. Please try again."
      );
    }
  };

  const handleSaveEditedComplete = async () => {
    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      await dispatch(updateSoapNote([appointment, user, note])).unwrap();
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();

      // Update the local state with the new appointment data
      setAppointment(updatedAppointment);
      setIsEditingCompleted(false);
      setShowApprovalView(true);

      toast.success("SOAP Note changes saved successfully.");
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(
        "An error occurred while saving the SOAP Note. Please try again."
      );
    }
  };

  const handleApprove = async () => {
    const confirmed = window.confirm(
      "This action cannot be undone. Once approved, the SOAP Note cannot be edited."
    );
    if (!confirmed) return;

    try {
      // Get the final note data - either from current state or from the existing SOAP note
      let finalNote;

      if (showApprovalView) {
        // Use data from the current state if in approval view
        finalNote = {
          noteData: isFreeform
            ? soapData.reduce((acc, field) => {
                acc[field.name] = field.value;
                return acc;
              }, {})
            : structuredData,
          templateUsed: isFreeform ? "freeform" : "structured",
        };
      } else {
        // Use existing SOAP note data if approving from edit view
        const latestNote =
          appointment.soapNote[appointment.soapNote.length - 1];
        finalNote = {
          noteData: latestNote.noteData,
          templateUsed: latestNote.templateUsed,
        };
      }

      await dispatch(
        approveSoapNote({
          appointment,
          user,
          finalNote,
          supervisorComment,
        })
      ).unwrap();
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      setAppointment(updatedAppointment);
      toast.success("SOAP Note Approved successfully.");
      onClose();
    } catch (error) {
      console.error("Approve SOAP Note failed:", error);
    }
  };

  const handleUnapprove = async () => {
    if (!unapproveReason.trim()) {
      toast.error("Please provide a reason for unapproving the SOAP note.");
      return;
    }

    try {
      await dispatch(
        unapproveSoapNote({
          appointment,
          user,
          reason: unapproveReason,
        })
      ).unwrap();
      
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      console.log(`[SOAPForm] After unapprove - updated appointment:`, updatedAppointment);
      setAppointment(updatedAppointment);
      setShowUnapproveModal(false);
      setUnapproveReason("");
      setShowApprovalView(false); // Switch to edit view
      toast.success("SOAP Note unapproved successfully. You can now edit it.");
    } catch (error) {
      console.error("Unapprove SOAP Note failed:", error);
      toast.error("Failed to unapprove SOAP note. Please try again.");
    }
  };

  const canApproveNote =
    appointment.soapNoteCompleted &&
    (user.specialization?.includes("SLP") ||
      (appointment.supervisingClinician &&
        user._id === appointment.supervisingClinician));

  // Check if SOAP note is already approved by looking at both soapNoteFinalized and status array
  const isApproved =
    appointment.soapNoteFinalized ||
    appointment.status?.includes("Soap Notes Approved");
  
  // Debug logging for unapprove issue
  console.log(`[SOAPForm] Debug - soapNoteFinalized: ${appointment.soapNoteFinalized}, status includes approved: ${appointment.status?.includes("Soap Notes Approved")}, isApproved: ${isApproved}`);

  // Check if current user can unapprove (SLP or owner users only)
  const canUnapprove =
    user.specialization?.includes("SLP") ||
    user.role?.includes("owner");

  // NO ONE can edit approved notes - once approved, it's completely read-only
  // Users who can unapprove should also be able to edit unapproved notes
  const canEditNote =
    !isApproved && (!appointment.soapNoteCompleted || canApproveNote || canUnapprove);

  const initializeHeidi = async () => {
    try {
      setHeidiLoading(true);

      // Get current SOAP note data
      let noteData;
      if (isFreeform) {
        noteData = {
          subjective: soapData
            .filter((field) => field.name.toLowerCase().includes("subjective"))
            .map((field) => field.value)
            .join("\n"),
          objective: soapData
            .filter((field) => field.name.toLowerCase().includes("objective"))
            .map((field) => field.value)
            .join("\n"),
          assessment: soapData
            .filter((field) => field.name.toLowerCase().includes("assessment"))
            .map((field) => field.value)
            .join("\n"),
          plan: soapData
            .filter((field) => field.name.toLowerCase().includes("plan"))
            .map((field) => field.value)
            .join("\n"),
        };
      } else {
        noteData = structuredData;
      }

      // Get Heidi token
      const token = await heidiService.getHeidiToken();

      // Update the Heidi widget's token
      if (window.Heidi) {
        window.Heidi.updateToken(token);
      } else {
        console.error("Heidi widget not loaded");
        return;
      }

      // Prepare data for Heidi
      const heidiData = {
        content: `
          Patient Name: ${appointment.client?.firstName} ${
          appointment.client?.lastName
        }
          Date: ${new Date(appointment.start).toLocaleDateString()}
          
          Subjective: ${noteData.subjective || ""}
          
          Objective: ${noteData.objective || ""}
          
          Assessment: ${noteData.assessment || ""}
          
          Plan: ${noteData.plan || ""}
        `,
        responses: [],
      };

      // Set up data handler
      window.Heidi.onPushData(async (data) => {
        try {
          // Save the analysis results
          await heidiService.saveHeidiAnalysis(
            appointment.client._id,
            appointment._id,
            data
          );

          // If Heidi provided structured SOAP note suggestions, update the form
          if (data.soapNote) {
            if (isFreeform) {
              const newSoapData = [];
              if (data.soapNote.subjective) {
                newSoapData.push({
                  id: Date.now(),
                  name: "Subjective",
                  value: data.soapNote.subjective,
                });
              }
              if (data.soapNote.objective) {
                newSoapData.push({
                  id: Date.now() + 1,
                  name: "Objective",
                  value: data.soapNote.objective,
                });
              }
              if (data.soapNote.assessment) {
                newSoapData.push({
                  id: Date.now() + 2,
                  name: "Assessment",
                  value: data.soapNote.assessment,
                });
              }
              if (data.soapNote.plan) {
                newSoapData.push({
                  id: Date.now() + 3,
                  name: "Plan",
                  value: data.soapNote.plan,
                });
              }
              setSoapData(newSoapData);
            } else {
              setStructuredData(data.soapNote);
            }
          }
        } catch (error) {
          console.error("Error handling Heidi data:", error);
        }
      });

      // Open Heidi with the template data
      window.Heidi.open({ template: heidiData });
    } catch (error) {
      console.error("Error initializing Heidi:", error);
      toast.error("Error initializing Heidi Health. Please try again.");
    } finally {
      setHeidiLoading(false);
    }
  };

  const renderSupervisorComment = () => {
    // Only show supervisor comment section when NOT in approval view and for completed/approved notes
    if (!showApprovalView && (isApproved || appointment.soapNoteCompleted)) {
      return (
        <div className="border border-blue-200 bg-blue-50 p-6 mt-6">
          <label className="block text-sm font-medium text-blue-900 mb-3">
            Supervisor Comments
          </label>
          <div className="bg-white border border-gray-200 p-4 text-sm text-gray-900 whitespace-pre-wrap min-h-[60px]">
            {supervisorComment && supervisorComment.trim() ? (
              supervisorComment
            ) : (
              <span className="text-gray-400 italic">
                No supervisor comments added
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-blue-700">
            Comments from the supervising clinician during approval.
          </p>
        </div>
      );
    }

    return null;
  };

  const renderClientInfo = () => (
    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-sm mb-4">
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">Client:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.client.firstName} {appointment.client.lastName}
          </span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Session Date:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.start
              ? new Date(appointment.start).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  weekday: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })
              : "N/A"}
          </span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Treating Clinician:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.clinician.firstName} {appointment.clinician.lastName}
          </span>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">D.O.B:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.client.dateOfBirth
              ? appointment.client.dateOfBirth.split("T")[0]
              : "N/A"}
          </span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">Duration:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.end && appointment.start
              ? `${Math.round(
                  (new Date(appointment.end) - new Date(appointment.start)) /
                    60000
                )} min`
              : "N/A"}
          </span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Supervising Clinician:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.supervisingClinician
              ? `${appointment.supervisingClinician.firstName} ${appointment.supervisingClinician.lastName}`
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );

  const renderActionButtons = () => (
    <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
      {!showApprovalView && !isSignedOff && (
        <>
          <button
            onClick={handleSave}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
            Save Draft
          </button>

          <button
            onClick={initializeHeidi}
            disabled={heidiLoading}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium transition-colors ${
              heidiLoading
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            }`}>
            <BeakerIcon className="h-4 w-4 mr-2" />
            {heidiLoading ? "Analyzing..." : "Analyze with Heidi"}
          </button>

          <button
            onClick={
              user.specialization?.includes("SLP")
                ? handleCompleteAndApproveSoapNote
                : handleCompleteSoapNote
            }
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            {user.specialization?.includes("SLP")
              ? "Complete & Approve SOAP"
              : "Complete Note"}
          </button>
        </>
      )}

      {showApprovalView && !isSignedOff && canApproveNote && (
        <button
          onClick={handleApprove}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
          Approve Note
        </button>
      )}

      {/* Special button for SLP users when SOAP is completed but not approved */}
      {!showApprovalView &&
        appointment.soapNoteCompleted &&
        !isApproved &&
        user.specialization?.includes("SLP") && (
          <button
            onClick={handleApprove}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            Approve SOAP
          </button>
        )}

      <button
        onClick={onClose}
        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
        Close
      </button>
    </div>
  );

  const renderEditView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">SOAP Note</h2>
            <p className="mt-1 text-sm text-gray-500">
              {isFreeform ? "Freeform" : "Structured"} format
              {isApproved && " (Read-Only)"}
            </p>
          </div>
          {isApproved && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 border border-green-200 text-sm font-medium text-green-800">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="font-semibold uppercase tracking-wide">
                  APPROVED
                </span>
              </div>
              {/* Unapprove button - only for SLP or owner users in emergency situations */}
              {canUnapprove && (
                <button
                  onClick={() => setShowUnapproveModal(true)}
                  className="inline-flex items-center px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded border border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Emergency unapprove - use only when necessary">
                  Unapprove
                </button>
              )}
            </div>
          )}
        </div>
        {isNewSoapNote && !appointment.soapNoteCompleted && (
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={!isFreeform}
                onChange={() => setIsFreeform(false)}
                className="form-radio h-4 w-4 text-indigo-600"
              />
              <span className="ml-2 text-sm text-gray-700">Structured</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={isFreeform}
                onChange={() => setIsFreeform(true)}
                className="form-radio h-4 w-4 text-indigo-600"
              />
              <span className="ml-2 text-sm text-gray-700">Freeform</span>
            </label>
          </div>
        )}
      </div>

      {isApproved ? (
        // Read-only view for approved notes
        <div className="space-y-6">
          <div className="border border-gray-200 bg-white">
            <div className="bg-green-50 border-b border-green-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  SOAP Note - Final
                </h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 border border-green-200 text-sm font-medium text-green-800">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Approved & Finalized
                </div>
              </div>
            </div>

            {isFreeform ? (
              <div className="divide-y divide-gray-100">
                {soapData.map((field, index) => (
                  <div key={field.id} className="p-6">
                    <div className="mb-4">
                      <div className="inline-flex items-center px-3 py-1.5 bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-900">
                        {field.name}
                      </div>
                    </div>
                    <div
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed [&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3 [&>h1]:mb-4 [&>h2]:mb-3 [&>h3]:mb-3 [&>h4]:mb-2 [&>h5]:mb-2 [&>h6]:mb-2 [&>blockquote]:mb-4 [&>pre]:mb-4 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-1"
                      dangerouslySetInnerHTML={{ __html: field.value }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {Object.entries(structuredData).map(([key, value]) => (
                  <div key={key} className="p-6">
                    <div className="mb-4">
                      <div className="inline-flex items-center px-3 py-1.5 bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-900">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </div>
                    </div>
                    <div
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed [&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3 [&>h1]:mb-4 [&>h2]:mb-3 [&>h3]:mb-3 [&>h4]:mb-2 [&>h5]:mb-2 [&>h6]:mb-2 [&>blockquote]:mb-4 [&>pre]:mb-4 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-1"
                      dangerouslySetInnerHTML={{ __html: value }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Editable view for non-approved notes
        <>
          {isFreeform ? (
            <div className="space-y-4">
              {soapData.map((field, index) => (
                <div key={field.id} className="space-y-2">
                  <input
                    type="text"
                    placeholder={`Field Name ${index + 1}`}
                    value={field.name}
                    onChange={(e) =>
                      setSoapData((prev) =>
                        prev.map((item) =>
                          item.id === field.id
                            ? { ...item, name: e.target.value }
                            : item
                        )
                      )
                    }
                    disabled={!canEditNote}
                    className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                  <ReactQuill
                    value={field.value}
                    onChange={(content) =>
                      setSoapData((prev) =>
                        prev.map((item) =>
                          item.id === field.id
                            ? { ...item, value: content }
                            : item
                        )
                      )
                    }
                    readOnly={!canEditNote}
                    theme="snow"
                  />
                </div>
              ))}
              {canEditNote && !appointment.soapNoteCompleted && (
                <button
                  type="button"
                  onClick={handleAddField}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  Add Field
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(structuredData).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </label>
                  <ReactQuill
                    value={value}
                    onChange={(content) =>
                      setStructuredData((prev) => ({
                        ...prev,
                        [key]: content,
                      }))
                    }
                    readOnly={!canEditNote}
                    theme="snow"
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!isApproved && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handlePopulateSoap}
              disabled={!appointment.blockId || !canEditNote}
              className={`inline-flex items-center px-2.5 py-1.5 text-sm font-medium rounded-sm shadow-sm transition-colors ${
                appointment.blockId && canEditNote
                  ? "bg-indigo-600 text-white hover:bg-indigo-700"
                  : "bg-gray-100 text-gray-500 cursor-not-allowed"
              }`}>
              Populate from Previous
            </button>
          </div>
          {canEditNote && (
            <div className="flex items-center space-x-2">
              {isEditingCompleted ? (
                <>
                  <button
                    type="button"
                    onClick={handleSaveEditedComplete}
                    className="inline-flex items-center px-2.5 py-1.5 bg-orange-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-orange-700 transition-colors">
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingCompleted(false);
                      setShowApprovalView(true);
                    }}
                    className="inline-flex items-center px-2.5 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-gray-700 transition-colors">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="inline-flex items-center px-2.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-indigo-700 transition-colors">
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={
                      user.specialization?.includes("SLP")
                        ? handleCompleteAndApproveSoapNote
                        : handleCompleteSoapNote
                    }
                    className="inline-flex items-center px-2.5 py-1.5 bg-green-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-green-700 transition-colors">
                    {user.specialization?.includes("SLP")
                      ? "Complete & Approve"
                      : "Complete"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Special Approve button for SLP users when SOAP is completed but not approved */}
      {!isApproved &&
        appointment.soapNoteCompleted &&
        !showApprovalView &&
        user.specialization?.includes("SLP") && (
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleApprove}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-green-700 transition-colors">
              Approve SOAP
            </button>
          </div>
        )}

      {renderActionButtons()}
    </div>
  );

  const renderApprovalView = () => (
    <div className="space-y-6">
      {/* Header with APPROVED indicator */}
      {isApproved && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            SOAP Note Review
          </h2>
          <div className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-sm">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-semibold text-sm uppercase tracking-wide">
              APPROVED
            </span>
          </div>
        </div>
      )}

      <div
        className={`${
          isApproved
            ? "bg-green-50 border border-green-200"
            : "bg-yellow-50 border border-yellow-200"
        } p-4`}>
        <div className="flex items-start gap-3">
          <div
            className={`flex-shrink-0 w-5 h-5 ${
              isApproved ? "text-green-400" : "text-yellow-400"
            }`}>
            {isApproved ? (
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div>
            <p
              className={`text-sm font-medium ${
                isApproved ? "text-green-800" : "text-yellow-800"
              }`}>
              {isApproved ? "SOAP Note Finalized" : "Ready for Approval"}
            </p>
            <p
              className={`text-sm mt-1 ${
                isApproved ? "text-green-700" : "text-yellow-700"
              }`}>
              {isApproved
                ? "This SOAP note has been approved and finalized. No further edits are allowed."
                : "This SOAP note is completed and ready for approval. Please review the content below and approve if everything is correct."}
            </p>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 bg-white">
        {isFreeform ? (
          <div className="divide-y divide-gray-100">
            {soapData.map((field, index) => (
              <div key={field.id} className="p-6">
                <div className="mb-4">
                  <div className="inline-flex items-center px-3 py-1.5 bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-900">
                    {field.name}
                  </div>
                </div>
                <div
                  className="prose prose-sm max-w-none text-gray-700 leading-relaxed [&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3 [&>h1]:mb-4 [&>h2]:mb-3 [&>h3]:mb-3 [&>h4]:mb-2 [&>h5]:mb-2 [&>h6]:mb-2 [&>blockquote]:mb-4 [&>pre]:mb-4 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: field.value }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(structuredData).map(([key, value], index) => (
              <div key={key} className="p-6">
                <div className="mb-4">
                  <div className="inline-flex items-center px-3 py-1.5 bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-900">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </div>
                </div>
                <div
                  className="prose prose-sm max-w-none text-gray-700 leading-relaxed [&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3 [&>h1]:mb-4 [&>h2]:mb-3 [&>h3]:mb-3 [&>h4]:mb-2 [&>h5]:mb-2 [&>h6]:mb-2 [&>blockquote]:mb-4 [&>pre]:mb-4 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Supervisor Comment Field - Only show for pending approval */}
      {!isApproved && (
        <div className="border border-gray-200 bg-gray-50 p-6">
          <label className="block text-sm font-medium text-gray-900 mb-3">
            Supervisor Comments (Optional)
          </label>
          <textarea
            value={supervisorComment}
            onChange={(e) => setSupervisorComment(e.target.value)}
            rows={4}
            readOnly={false}
            disabled={false}
            className="block w-full border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
            placeholder="Add any comments or feedback regarding this SOAP note..."
            style={{ pointerEvents: 'auto' }}
          />
          <p className="mt-2 text-xs text-gray-600">
            These comments will be saved with the approval and can be viewed in
            the appointment history.
          </p>
        </div>
      )}

      {renderActionButtons()}
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 z-70"
        onClick={onClose}
      />
      <div data-component="SOAPForm" className="relative bg-white rounded-sm shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto z-71">
        <div className="absolute top-0 right-0 pt-4 pr-4">
          <button
            onClick={onClose}
            className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {renderClientInfo()}
          {showApprovalView ? renderApprovalView() : renderEditView()}
          {renderSupervisorComment()}
        </div>
      </div>

      {/* Unapprove Modal */}
      {showUnapproveModal && (
        <div className="fixed inset-0 flex items-center justify-center z-80 overflow-y-auto">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 z-80"
            onClick={() => setShowUnapproveModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 z-81">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Unapprove SOAP Note
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This action will allow the SOAP note to be edited again. Use only in emergency situations.
              </p>
            </div>
            
            <div className="px-6 py-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Warning: Emergency Action
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        This will unapprove an already approved SOAP note, which may impact audit trails and compliance. 
                        Only proceed if absolutely necessary.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for unapproving (required)
              </label>
              <textarea
                value={unapproveReason}
                onChange={(e) => setUnapproveReason(e.target.value)}
                rows={4}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-500 focus:ring-offset-0"
                placeholder="Explain why this SOAP note needs to be unapproved..."
              />
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUnapproveModal(false);
                  setUnapproveReason("");
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                Cancel
              </button>
              <button
                onClick={handleUnapprove}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500">
                Unapprove SOAP Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOAPForm;
